import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Menu, Search, ShoppingBag, UserRound, X } from 'lucide-react';
import { LANGS, useLang } from '../i18n';
import { useVisibleDestinations } from '../data/catalog';
import { SearchOverlay } from './SearchOverlay';
import { CurrencySelector } from './CurrencySelector';
import { useCart } from '../utils/cart';
import { getAuthUser, logout } from '../utils/auth';

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const authUser = getAuthUser();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const langRef = useRef(null);
  const visibleDestinations = useVisibleDestinations();
  const { count: cartCount } = useCart();
  const [inspirations, setInspirations] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inspiration-categories`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setInspirations((Array.isArray(d) ? d : []).filter((c) => c.experience_slugs.length > 0)))
      .catch(() => {});
  }, []);

  const destChildren = [
    ...visibleDestinations.flatMap((d) => (d.subs?.length ? d.subs : [d]).map((s) => ({ label: s.name, to: d.subs?.length ? `/destinations/${d.slug}/${s.slug}` : `/destinations/${d.slug}`, tid: `destination-${s.slug}` }))),
    { hr: true },
    { label: t('nav.all.dest'), to: '/destinations', tid: 'destinations-all' },
  ];
  const expChildren = [
    { label: t('nav.all.exp'), to: '/experiences', tid: 'experiences-all' },
    ...(inspirations.length ? [{ header: t('nav.inspiration') }] : []),
    ...inspirations.map((c) => ({ label: c.name[lang] || c.name.pt, to: `/experiences?inspiration=${c.slug}`, tid: `inspiration-${c.slug}` })),
    { hr: true },
    { label: t('nav.tours'), to: '/journeys', tid: 'journeys' },
  ];
  const aboutChildren = [
    { label: t('nav.ourstory'), to: '/about#our-story', tid: 'about-story' },
    { label: t('nav.howworks'), to: '/#how-it-works', tid: 'about-how' },
    { label: t('nav.network'), to: '/partners', tid: 'about-network' },
    { label: t('nav.b2b'), to: '/travel-trade', tid: 'about-b2b' },
  ];
  const planningChildren = [
    { label: t('nav.journeys'), to: '/journeys', tid: 'planning-journeys' },
    { label: t('nav.stays'), to: '/accommodations', tid: 'planning-stays' },
    { label: t('nav.flights'), to: '/flights', tid: 'planning-flights' },
    { label: t('nav.transfers'), to: '/transfers', tid: 'planning-transfers' },
    { label: t('journey.build'), to: '/build-your-journey', tid: 'planning-builder' },
  ];

  const LINKS = [
    { label: t('nav.destinations'), key: 'destinos', to: '/destinations', children: destChildren },
    { label: t('nav.experiences'), key: 'experiencias', to: '/experiences', children: expChildren },
    { label: t('nav.travelPlanning'), key: 'planear', to: '/build-your-journey', children: planningChildren },
    { label: t('nav.travelTrade'), key: 'trade', to: '/travel-trade' },
    { label: t('nav.aboutus'), key: 'sobre', to: '/about', children: aboutChildren },
  ];
  const MOBILE_LINKS = LINKS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const close = (e) => { if (!langRef.current?.contains(e.target)) setLangOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const dark = scrolled || open || !isHome;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        dark ? 'bg-eco-cream/90 backdrop-blur-xl shadow-[0_1px_0_rgba(30,42,36,0.08)]' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center gap-2.5 whitespace-nowrap xl:mr-6">
          <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-eco-ink/10">
            <img src="/logo-eco-emblem.png" alt="Eco Collection" className="h-8 w-8 object-contain" />
          </span>
          <span className="flex flex-col justify-center leading-none">
            <span className={`text-base font-semibold tracking-[-0.01em] transition-colors ${dark ? 'text-eco-ink' : 'text-white'}`}>
              Eco Collection
            </span>
            <span data-testid="nav-brand-sub" className={`mt-1 text-[8px] font-bold uppercase tracking-[0.34em] transition-colors ${dark ? 'text-eco-gold' : 'text-eco-gold'}`}>
              Travel Network
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-4 whitespace-nowrap xl:flex 2xl:gap-5">
          {LINKS.map((l) => {
            const active = location.pathname === l.to || location.pathname.startsWith(`${l.to}/`) || (l.children?.some((c) => location.pathname === c.to) ?? false);
            return (
            <div key={l.key} className="group relative">
              <Link
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                aria-current={active ? 'page' : undefined}
                className={`relative flex items-center gap-1 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-200 hover:text-eco-gold ${
                  active ? 'text-eco-gold after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-eco-gold' : dark ? 'text-eco-ink/80' : 'text-white/85'
                }`}
              >
                {l.label}
                {l.children && <ChevronDown size={13} className="opacity-60" />}
              </Link>
              {l.children && (
                <div className="invisible absolute left-0 pt-1 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                  <div className="w-64 overflow-hidden rounded-xl border border-eco-ink/10 bg-white shadow-xl">
                    {l.children.map((c, ci) => {
                      if (c.hr) return <div key={`hr-${ci}`} className="my-1 border-t border-eco-ink/10" />;
                      if (c.header) return <p key={`h-${ci}`} className="px-4 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-eco-ink/40">{c.header}</p>;
                      return (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-dropdown-${c.tid}`}
                          aria-current={location.pathname === c.to ? 'page' : undefined}
                          className={`block px-4 py-2.5 text-sm transition-colors hover:bg-eco-cream hover:text-eco-green ${location.pathname === c.to ? 'bg-eco-green/5 font-semibold text-eco-green' : 'text-eco-ink/75'}`}
                        >
                          {c.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </nav>

        <div className="hidden items-center gap-4 whitespace-nowrap xl:ml-6 xl:flex xl:gap-5 2xl:gap-7">
          <button
            type="button"
            data-testid="nav-search-button"
            aria-label={t('nav.search')}
            onClick={() => setSearchOpen(true)}
            className={`grid h-9 w-9 place-items-center rounded-full transition-colors hover:text-eco-gold ${dark ? 'text-eco-ink/80 hover:bg-eco-green/5' : 'text-white/85 hover:bg-white/10'}`}
          >
            <Search size={18} />
          </button>
          <CurrencySelector dark={dark} />
          <Link
            to="/my-trip"
            data-testid="nav-cart-button"
            aria-label={t('cart.label')}
            className={`relative grid h-9 w-9 place-items-center rounded-full transition-colors hover:text-eco-gold ${dark ? 'text-eco-ink/80 hover:bg-eco-green/5' : 'text-white/85 hover:bg-white/10'}`}
          >
            <ShoppingBag size={18} />
            {cartCount > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-eco-gold px-1 text-[10px] font-bold text-eco-deep">
                {cartCount}
              </span>
            )}
          </Link>
          <div ref={langRef} className="relative">
            <button
              data-testid="nav-language-button"
              onClick={(e) => { e.stopPropagation(); setLangOpen(!langOpen); }}
              className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-eco-gold ${dark ? 'text-eco-ink/80' : 'text-white/85'}`}
            >
              {lang.toUpperCase()} <ChevronDown size={13} className="opacity-60" />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.2 }}
                  data-testid="nav-language-menu"
                  className="absolute right-0 mt-3 w-28 overflow-hidden rounded-xl border border-eco-ink/10 bg-white shadow-xl"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`nav-lang-${l.id}`}
                      onClick={() => { setLang(l.id); setLangOpen(false); }}
                      className={`block w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-eco-cream ${
                        lang === l.id ? 'font-semibold text-eco-green' : 'text-eco-ink/70'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="group relative">
            <button
              type="button"
              data-testid="nav-login-button"
              className={`flex items-center gap-1.5 py-2 text-sm font-medium transition-colors hover:text-eco-gold ${dark ? 'text-eco-ink/80' : 'text-white/85'}`}
            >
              <UserRound size={17} /> {authUser ? (authUser.name || authUser.email) : t('nav.login')} <ChevronDown size={12} className="opacity-60" />
            </button>
            <div className="invisible absolute right-0 pt-1 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
              <div className="w-52 overflow-hidden rounded-xl border border-eco-ink/10 bg-white shadow-xl">
                {!authUser && (
                  <>
                    <Link
                      to="/my-trip"
                      data-testid="nav-account-mytrip"
                      aria-current={location.pathname === '/my-trip' ? 'page' : undefined}
                      className={`block px-4 py-2.5 text-sm transition-colors hover:bg-eco-cream hover:text-eco-green ${location.pathname === '/my-trip' ? 'bg-eco-green/5 font-semibold text-eco-green' : 'text-eco-ink/75'}`}
                    >
                      {t('nav.mytrip')}
                    </Link>
                    <Link
                      to="/partner"
                      data-testid="nav-account-partner"
                      className={`block px-4 py-2.5 text-sm transition-colors hover:bg-eco-cream hover:text-eco-green ${location.pathname === '/partner' ? 'bg-eco-green/5 font-semibold text-eco-green' : 'text-eco-ink/75'}`}
                    >
                      {t('nav.partner.area')}
                    </Link>
                    <Link
                      to="/admin"
                      data-testid="nav-account-admin"
                      className={`block px-4 py-2.5 text-sm transition-colors hover:bg-eco-cream hover:text-eco-green ${location.pathname === '/admin' ? 'bg-eco-green/5 font-semibold text-eco-green' : 'text-eco-ink/75'}`}
                    >
                      {t('nav.admin.area')}
                    </Link>
                  </>
                )}
                {authUser && (
                  <>
                    <p className="border-b border-eco-ink/10 px-4 py-2.5 text-xs text-eco-ink/50" data-testid="nav-account-user">{authUser.email}</p>
                    <Link to={authUser.role === 'admin' ? '/admin' : '/partner'} data-testid="nav-account-dashboard" className="block px-4 py-2.5 text-sm text-eco-ink/75 transition-colors hover:bg-eco-cream hover:text-eco-green">
                      {authUser.role === 'admin' ? 'Admin Dashboard' : t('nav.partner.area')}
                    </Link>
                    <button
                      type="button"
                      data-testid="nav-account-logout"
                      onClick={() => logout(navigate)}
                      className="block w-full px-4 py-2.5 text-left text-sm text-red-500 transition-colors hover:bg-red-50"
                    >
                      {t('auth.logout')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <Link
            to="/build-your-journey"
            data-testid="nav-plan-button"
            className="inline-flex min-h-11 items-center rounded-[10px] bg-eco-gold px-5 text-sm font-semibold whitespace-nowrap text-eco-deep transition-colors duration-200 hover:bg-[#b8944f] focus:outline-none focus:ring-2 focus:ring-white/70"
          >
            {t('cta.proposal')}
          </Link>
        </div>

        <div className="flex items-center gap-3 xl:hidden">
          <CurrencySelector dark={dark} compact testidPrefix="nav-currency-mobile" />
          <Link
            to="/my-trip"
            data-testid="nav-cart-button-mobile"
            aria-label={t('cart.label')}
            className={`relative grid h-9 w-9 place-items-center rounded-full ${dark ? 'text-eco-ink' : 'text-white'}`}
          >
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <span data-testid="nav-cart-count-mobile" className="absolute -top-0.5 -right-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-eco-gold px-1 text-[10px] font-bold text-eco-deep">
                {cartCount}
              </span>
            )}
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            aria-label="Menu"
            onClick={() => setOpen(!open)}
            className={`${dark ? 'text-eco-ink' : 'text-white'}`}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[calc(100vh-4.5rem)] overflow-y-auto overscroll-contain border-t border-eco-ink/10 bg-eco-cream shadow-lg xl:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 bg-eco-cream px-5 py-4">
              <button
                type="button"
                data-testid="nav-mobile-search-button"
                onClick={() => { setOpen(false); setSearchOpen(true); }}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-eco-ink/85 hover:bg-eco-green/5"
              >
                <Search size={16} /> {t('nav.search')}
              </button>
              {MOBILE_LINKS.map((l) => (
                <div key={l.key}>
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.key}`}
                    aria-current={location.pathname === l.to ? 'page' : undefined}
                    className={`block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-eco-green/5 ${location.pathname === l.to ? 'bg-eco-green/10 text-eco-green' : 'text-eco-ink/85'}`}
                  >
                    {l.label}
                  </Link>
                  {l.children?.map((c, ci) => {
                    if (c.hr) return null;
                    if (c.header) return <p key={`mh-${ci}`} className="pr-3 pl-7 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-eco-ink/40">{c.header}</p>;
                    return (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-mobile-link-${c.tid}`}
                        aria-current={location.pathname === c.to ? 'page' : undefined}
                        className={`block rounded-lg py-2 pr-3 pl-7 text-sm hover:bg-eco-green/5 ${location.pathname === c.to ? 'font-semibold text-eco-green' : 'text-eco-ink/75'}`}
                      >
                        {c.label}
                      </Link>
                    );
                  })}
                </div>
              ))}
              <Link to="/my-trip" data-testid="nav-mobile-link-minha-viagem" className={`block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-eco-green/5 ${location.pathname === '/my-trip' ? 'bg-eco-green/10 text-eco-green' : 'text-eco-ink/85'}`}>
                {t('nav.mytrip')}
              </Link>
              <Link to="/partner" data-testid="nav-mobile-link-partner-area" className={`block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-eco-green/5 ${location.pathname === '/partner' ? 'bg-eco-green/10 text-eco-green' : 'text-eco-ink/85'}`}>
                {t('nav.partner.area')}
              </Link>
              <Link to="/admin" data-testid="nav-mobile-link-admin" className={`block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-eco-green/5 ${location.pathname === '/admin' ? 'bg-eco-green/10 text-eco-green' : 'text-eco-ink/85'}`}>
                {t('nav.admin.area')}
              </Link>
              <div className="mt-2 px-3 pb-2">
                <Link
                  to="/build-your-journey"
                  data-testid="nav-mobile-plan-button"
                  className="block min-h-12 rounded-[10px] bg-eco-gold px-5 py-3 text-center text-[15px] font-semibold text-eco-deep transition-colors duration-200 hover:bg-[#b8944f]"
                >
                  {t('cta.proposal')}
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
